import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Tabs, 
  Tab, 
  TextField, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  FormControlLabel,
  Checkbox,
  Switch,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  TextareaAutosize,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { 
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  QrCodeScanner as QrCodeScannerIcon,
  Image as ImageIcon,
  PhotoCamera as PhotoCameraIcon,
  Code as CodeIcon,
  Article as ArticleIcon,
  Lock as LockIcon,
  ShoppingCart as ShoppingCartIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import ProductManagement from './ProductManagement';
import ClientManagement from './ClientManagement';

const BackendManagement = () => {
  const { isSuperUser } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [users, setUsers] = useState([]);
  const [banners, setBanners] = useState([]);
  const [newsArticles, setNewsArticles] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [currentItem, setCurrentItem] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    role: '',
    password: '',
    title: '',
    description: '',
    imageUrl: '',
    link: '',
    content: '',
    summary: '',
    author: '',
    category: '',
    date: new Date().toISOString().split('T')[0]
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Use MUI theme and responsive breakpoints
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  useEffect(() => {
    // Load users
    const savedUsers = localStorage.getItem('users');
    if (savedUsers) {
      setUsers(JSON.parse(savedUsers));
    }

    // Load banners
    const savedBanners = localStorage.getItem('banners');
    if (savedBanners) {
      setBanners(JSON.parse(savedBanners));
    }

    // Load news articles
    const savedNews = localStorage.getItem('newsArticles');
    if (savedNews) {
      setNewsArticles(JSON.parse(savedNews));
    }
  }, []);

  const handleTabChange = (event, newValue) => {
    // Restrict access to News & Articles tab (tab index 2) and Products tab (tab index 3) for non-superusers
    if ((newValue === 2 || newValue === 3) && !isSuperUser()) {
      setError('Only Super Users can access this section');
      return;
    }
    
    setActiveTab(newValue);
    setError('');
  };

  const handleOpenDialog = (type, item = null) => {
    setDialogType(type);
    setCurrentItem(item);
    
    if (item) {
      // Populate form data for editing
      if (type === 'editUser') {
        setFormData({
          username: item.username,
          role: item.role,
          password: ''
        });
      } else if (type === 'editBanner') {
        setFormData({
          title: item.title || '',
          description: item.description || '',
          imageUrl: item.imageUrl || '',
          link: item.link || ''
        });
      } else if (type === 'editNews') {
        setFormData({
          title: item.title || '',
          author: item.author || '',
          date: item.date || new Date().toISOString().split('T')[0],
          category: item.category || '',
          summary: item.summary || '',
          content: item.content || '',
          imageUrl: item.imageUrl || ''
        });
      }
    } else {
      // Clear form data for new items
      if (type === 'addUser') {
        setFormData({
          username: '',
          role: '',
          password: ''
        });
      } else if (type === 'addBanner') {
        setFormData({
          title: '',
          description: '',
          imageUrl: '',
          link: ''
        });
      } else if (type === 'addNews') {
        setFormData({
          title: '',
          author: '',
          date: new Date().toISOString().split('T')[0],
          category: '',
          summary: '',
          content: '',
          imageUrl: ''
        });
      }
    }
    
    setOpenDialog(true);
    setError('');
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    try {
      if (dialogType === 'addUser' || dialogType === 'editUser') {
        // Handle user form submission
        if (!formData.username || !formData.role || (dialogType === 'addUser' && !formData.password)) {
          setError('Please fill in all required fields');
          return;
        }

        let updatedUsers = [...users];
        
        if (dialogType === 'addUser') {
          // Add new user
          const newUser = {
            id: Date.now().toString(),
            username: formData.username,
            role: formData.role,
            password: formData.password
          };
          updatedUsers.push(newUser);
        } else {
          // Update existing user
          const index = users.findIndex(u => u.id === currentItem.id);
          if (index !== -1) {
            updatedUsers[index] = {
              ...users[index],
              username: formData.username,
              role: formData.role,
              ...(formData.password ? { password: formData.password } : {})
            };
          }
        }
        
        localStorage.setItem('users', JSON.stringify(updatedUsers));
        setUsers(updatedUsers);
      } else if (dialogType === 'addBanner' || dialogType === 'editBanner') {
        // Handle banner form submission
        if (!formData.title || !formData.imageUrl) {
          setError('Banner title and image URL are required');
          return;
        }

        let updatedBanners = [...banners];
        
        if (dialogType === 'addBanner') {
          // Add new banner
          const newBanner = {
            id: Date.now().toString(),
            title: formData.title,
            description: formData.description,
            imageUrl: formData.imageUrl,
            link: formData.link
          };
          updatedBanners.push(newBanner);
        } else {
          // Update existing banner
          const index = banners.findIndex(b => b.id === currentItem.id);
          if (index !== -1) {
            updatedBanners[index] = {
              ...banners[index],
              title: formData.title,
              description: formData.description,
              imageUrl: formData.imageUrl,
              link: formData.link
            };
          }
        }
        
        localStorage.setItem('banners', JSON.stringify(updatedBanners));
        setBanners(updatedBanners);
      } else if (dialogType === 'addNews' || dialogType === 'editNews') {
        // Handle news article form submission
        if (!formData.title || !formData.content || !formData.imageUrl) {
          setError('Title, content, and image URL are required');
          return;
        }

        let updatedNews = [...newsArticles];
        
        if (dialogType === 'addNews') {
          // Add new article
          const newArticle = {
            id: Date.now().toString(),
            title: formData.title,
            author: formData.author,
            date: formData.date,
            category: formData.category,
            summary: formData.summary,
            content: formData.content,
            imageUrl: formData.imageUrl
          };
          updatedNews.push(newArticle);
        } else {
          // Update existing article
          const index = newsArticles.findIndex(a => a.id === currentItem.id);
          if (index !== -1) {
            updatedNews[index] = {
              ...newsArticles[index],
              title: formData.title,
              author: formData.author,
              date: formData.date,
              category: formData.category,
              summary: formData.summary,
              content: formData.content,
              imageUrl: formData.imageUrl
            };
          }
        }
        
        localStorage.setItem('newsArticles', JSON.stringify(updatedNews));
        setNewsArticles(updatedNews);
      }
      
      setSuccess(dialogType.includes('add') ? 'Item added successfully' : 'Item updated successfully');
      setTimeout(() => {
        setSuccess('');
      }, 3000);
      
      handleCloseDialog();
    } catch (error) {
      setError('Error: ' + error.message);
    }
  };

  const handleDelete = (type, id) => {
    try {
      if (type === 'user') {
        const updatedUsers = users.filter(user => user.id !== id);
        localStorage.setItem('users', JSON.stringify(updatedUsers));
        setUsers(updatedUsers);
      } else if (type === 'banner') {
        const updatedBanners = banners.filter(banner => banner.id !== id);
        localStorage.setItem('banners', JSON.stringify(updatedBanners));
        setBanners(updatedBanners);
      } else if (type === 'news') {
        const updatedNews = newsArticles.filter(article => article.id !== id);
        localStorage.setItem('newsArticles', JSON.stringify(updatedNews));
        setNewsArticles(updatedNews);
      }
      
      setSuccess('Item deleted successfully');
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (error) {
      setError('Error: ' + error.message);
    }
  };

  const renderUserManagement = () => (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: isMobile ? 'flex-start' : 'center', 
        mb: 2,
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 1 : 0
      }}>
        <Typography variant="h6" sx={{ mb: isMobile ? 1 : 0 }}>User Management</Typography>
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog('addUser')}
          fullWidth={isMobile}
          size={isMobile ? "small" : "medium"}
        >
          Add User
        </Button>
      </Box>
      
      <Paper>
        <List>
          {users.map(user => (
            <ListItem key={user.id} divider>
              <ListItemText 
                primary={user.username} 
                secondary={`Role: ${user.role}`} 
              />
              <ListItemSecondaryAction>
                <IconButton 
                  edge="end" 
                  color="primary"
                  onClick={() => handleOpenDialog('editUser', user)}
                  size={isMobile ? "small" : "medium"}
                >
                  <EditIcon />
                </IconButton>
                <IconButton 
                  edge="end" 
                  color="error"
                  onClick={() => handleDelete('user', user.id)}
                  size={isMobile ? "small" : "medium"}
                >
                  <DeleteIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
          {users.length === 0 && (
            <ListItem>
              <ListItemText primary="No users found" />
            </ListItem>
          )}
        </List>
      </Paper>
    </Box>
  );

  const renderBannerManagement = () => (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: isMobile ? 'flex-start' : 'center', 
        mb: 2,
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 1 : 0
      }}>
        <Typography variant="h6" sx={{ mb: isMobile ? 1 : 0 }}>Banner Management</Typography>
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog('addBanner')}
          fullWidth={isMobile}
          size={isMobile ? "small" : "medium"}
        >
          Add Banner
        </Button>
      </Box>
      
      <Grid container spacing={2}>
        {banners.map(banner => (
          <Grid item xs={12} sm={6} md={4} key={banner.id}>
            <Card>
              <CardMedia
                component="img"
                height={isMobile ? "120" : "140"}
                image={banner.imageUrl}
                alt={banner.title}
              />
              <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
                <Typography variant={isMobile ? "subtitle1" : "h6"} component="div">
                  {banner.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {banner.description}
                </Typography>
              </CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
                <IconButton 
                  color="primary"
                  onClick={() => handleOpenDialog('editBanner', banner)}
                  size={isMobile ? "small" : "medium"}
                >
                  <EditIcon />
                </IconButton>
                <IconButton 
                  color="error"
                  onClick={() => handleDelete('banner', banner.id)}
                  size={isMobile ? "small" : "medium"}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Card>
          </Grid>
        ))}
        {banners.length === 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography>No banners found</Typography>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );

  const renderNewsManagement = () => {
    if (!isSuperUser()) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <LockIcon color="error" sx={{ fontSize: 60, mb: 2 }} />
          <Typography variant="h6" color="error" gutterBottom>
            Restricted Access
          </Typography>
          <Typography variant="body1">
            News and article management is restricted to Super Users only.
          </Typography>
        </Box>
      );
    }
    
    return (
      <Box>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: isMobile ? 'flex-start' : 'center', 
          mb: 2,
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 1 : 0
        }}>
          <Typography variant="h6" sx={{ mb: isMobile ? 1 : 0 }}>News & Articles Management</Typography>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog('addNews')}
            fullWidth={isMobile}
            size={isMobile ? "small" : "medium"}
          >
            Add Article
          </Button>
        </Box>
        
        <Grid container spacing={2}>
          {newsArticles.map(article => (
            <Grid item xs={12} sm={6} key={article.id}>
              <Card>
                <CardMedia
                  component="img"
                  height={isMobile ? "120" : "140"}
                  image={article.imageUrl}
                  alt={article.title}
                />
                <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
                  <Typography variant={isMobile ? "subtitle1" : "h6"} component="div" gutterBottom>
                    {article.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {new Date(article.date).toLocaleDateString()} | {article.category} | By {article.author}
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2" color="text.secondary" sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                  }}>
                    {article.summary}
                  </Typography>
                </CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
                  <IconButton 
                    color="primary"
                    onClick={() => handleOpenDialog('editNews', article)}
                    size={isMobile ? "small" : "medium"}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton 
                    color="error"
                    onClick={() => handleDelete('news', article.id)}
                    size={isMobile ? "small" : "medium"}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Card>
            </Grid>
          ))}
          {newsArticles.length === 0 && (
            <Grid item xs={12}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography>No news articles found</Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      </Box>
    );
  };

  const renderProductManagement = () => {
    return <ProductManagement />;
  };
  
  const renderClientManagement = () => {
    return <ClientManagement />;
  };

  const renderSystemSettings = () => (
    <Box>
      <Typography variant="h6" gutterBottom>System Settings</Typography>
      <Paper sx={{ p: 2 }}>
        <Typography variant="body1">System settings will be implemented in future updates.</Typography>
      </Paper>
    </Box>
  );

  const renderDialog = () => {
    const isUserDialog = dialogType === 'addUser' || dialogType === 'editUser';
    const isBannerDialog = dialogType === 'addBanner' || dialogType === 'editBanner';
    const isNewsDialog = dialogType === 'addNews' || dialogType === 'editNews';
    
    return (
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth={isNewsDialog ? 'md' : 'sm'}
        fullWidth
        fullScreen={isMobile && isNewsDialog}
      >
        <DialogTitle>
          {dialogType === 'addUser' && 'Add New User'}
          {dialogType === 'editUser' && 'Edit User'}
          {dialogType === 'addBanner' && 'Add New Banner'}
          {dialogType === 'editBanner' && 'Edit Banner'}
          {dialogType === 'addNews' && 'Add New Article'}
          {dialogType === 'editNews' && 'Edit Article'}
        </DialogTitle>
        
        <form onSubmit={handleSubmit}>
          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}
            
            {isUserDialog && (
              <>
                <TextField
                  autoFocus
                  margin="dense"
                  name="username"
                  label="Username"
                  type="text"
                  fullWidth
                  value={formData.username}
                  onChange={handleChange}
                  required
                  size={isMobile ? "small" : "medium"}
                />
                
                <FormControl fullWidth margin="dense" size={isMobile ? "small" : "medium"}>
                  <InputLabel>Role</InputLabel>
                  <Select
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    required
                  >
                    <MenuItem value="admin">Admin</MenuItem>
                    <MenuItem value="worker">Worker</MenuItem>
                    <MenuItem value="developer">Developer</MenuItem>
                    <MenuItem value="superuser">Super User</MenuItem>
                  </Select>
                </FormControl>
                
                <TextField
                  margin="dense"
                  name="password"
                  label={dialogType === 'addUser' ? 'Password' : 'New Password (leave blank to keep current)'}
                  type="password"
                  fullWidth
                  value={formData.password}
                  onChange={handleChange}
                  required={dialogType === 'addUser'}
                  size={isMobile ? "small" : "medium"}
                />
              </>
            )}
            
            {isBannerDialog && (
              <>
                <TextField
                  autoFocus
                  margin="dense"
                  name="title"
                  label="Banner Title"
                  type="text"
                  fullWidth
                  value={formData.title}
                  onChange={handleChange}
                  required
                  size={isMobile ? "small" : "medium"}
                />
                
                <TextField
                  margin="dense"
                  name="description"
                  label="Banner Description"
                  type="text"
                  fullWidth
                  value={formData.description}
                  onChange={handleChange}
                  size={isMobile ? "small" : "medium"}
                />
                
                <TextField
                  margin="dense"
                  name="imageUrl"
                  label="Image URL"
                  type="url"
                  fullWidth
                  value={formData.imageUrl}
                  onChange={handleChange}
                  required
                  helperText="Enter a valid image URL (e.g., https://example.com/image.jpg)"
                  size={isMobile ? "small" : "medium"}
                />
                
                <TextField
                  margin="dense"
                  name="link"
                  label="Banner Link (Optional)"
                  type="url"
                  fullWidth
                  value={formData.link}
                  onChange={handleChange}
                  helperText="Where should the banner link to when clicked?"
                  size={isMobile ? "small" : "medium"}
                />
              </>
            )}
            
            {isNewsDialog && (
              <>
                <TextField
                  autoFocus
                  margin="dense"
                  name="title"
                  label="Article Title"
                  type="text"
                  fullWidth
                  value={formData.title}
                  onChange={handleChange}
                  required
                  size={isMobile ? "small" : "medium"}
                />
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      margin="dense"
                      name="author"
                      label="Author"
                      type="text"
                      fullWidth
                      value={formData.author}
                      onChange={handleChange}
                      required
                      size={isMobile ? "small" : "medium"}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      margin="dense"
                      name="date"
                      label="Publication Date"
                      type="date"
                      fullWidth
                      value={formData.date}
                      onChange={handleChange}
                      required
                      InputLabelProps={{
                        shrink: true,
                      }}
                      size={isMobile ? "small" : "medium"}
                    />
                  </Grid>
                </Grid>
                
                <FormControl fullWidth margin="dense" size={isMobile ? "small" : "medium"}>
                  <InputLabel>Category</InputLabel>
                  <Select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    required
                  >
                    <MenuItem value="General">General</MenuItem>
                    <MenuItem value="Techniques">Techniques</MenuItem>
                    <MenuItem value="Sustainability">Sustainability</MenuItem>
                    <MenuItem value="Market">Market</MenuItem>
                    <MenuItem value="Research">Research</MenuItem>
                  </Select>
                </FormControl>
                
                <TextField
                  margin="dense"
                  name="summary"
                  label="Article Summary"
                  type="text"
                  fullWidth
                  multiline
                  rows={isMobile ? 2 : 2}
                  value={formData.summary}
                  onChange={handleChange}
                  required
                  helperText="A brief summary that appears in the article list"
                  size={isMobile ? "small" : "medium"}
                />
                
                <TextField
                  margin="dense"
                  name="content"
                  label="Article Content"
                  type="text"
                  fullWidth
                  multiline
                  rows={isMobile ? 4 : 6}
                  value={formData.content}
                  onChange={handleChange}
                  required
                  helperText="Use double line breaks (press Enter twice) to create new paragraphs"
                  size={isMobile ? "small" : "medium"}
                />
                
                <TextField
                  margin="dense"
                  name="imageUrl"
                  label="Featured Image URL"
                  type="url"
                  fullWidth
                  value={formData.imageUrl}
                  onChange={handleChange}
                  required
                  helperText="Enter a valid image URL (e.g., https://example.com/image.jpg)"
                  size={isMobile ? "small" : "medium"}
                />
              </>
            )}
          </DialogContent>
          
          <DialogActions>
            <Button onClick={handleCloseDialog} size={isMobile ? "small" : "medium"}>Cancel</Button>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
              startIcon={<SaveIcon />}
              size={isMobile ? "small" : "medium"}
            >
              Save
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    );
  };

  return (
    <Box sx={{ width: '100%' }}>
      {success && !openDialog && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}
      
      {error && !openDialog && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      <Paper sx={{ width: '100%', mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              minWidth: isMobile ? 'auto' : 120,
              fontSize: isMobile ? '0.75rem' : '0.875rem',
              padding: isMobile ? '6px 8px' : '12px 16px',
            },
          }}
        >
          <Tab label="User Management" />
          <Tab label="Banner Management" />
          <Tab label="Client Management" icon={<BusinessIcon />} iconPosition="start" />
          {isSuperUser() ? (
            <Tab label={isMobile ? "News" : "News & Articles"} icon={<ArticleIcon />} iconPosition="start" />
          ) : (
            <Tab label={isMobile ? "News" : "News & Articles"} icon={<ArticleIcon />} iconPosition="start" disabled />
          )}
          {isSuperUser() ? (
            <Tab label={isMobile ? "Products" : "Marketplace Products"} icon={<ShoppingCartIcon />} iconPosition="start" />
          ) : (
            <Tab label={isMobile ? "Products" : "Marketplace Products"} icon={<ShoppingCartIcon />} iconPosition="start" disabled />
          )}
          <Tab label="Settings" />
        </Tabs>
      </Paper>
      
      <Box sx={{ mt: 2 }}>
        {activeTab === 0 && renderUserManagement()}
        {activeTab === 1 && renderBannerManagement()}
        {activeTab === 2 && renderClientManagement()}
        {activeTab === 3 && renderNewsManagement()}
        {activeTab === 4 && renderProductManagement()}
        {activeTab === 5 && renderSystemSettings()}
      </Box>
      
      {renderDialog()}
    </Box>
  );
};

export default BackendManagement; 